import { useState, useEffect, useCallback } from "react";
import { apiV2Service } from "../../../../services/apiV2Service";
import * as localService from "../../../../services/localService";
import logger from "../../../../utils/logger";
import toastUtils from "../../../../utils/toast";
import { showApiMessage } from "../../../../utils/messageHandler";
import { HostClientsConstants } from "../../../../utils/constants";

export const useUserManagement = () => {
  // Invite modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteUserForm, setInviteUserForm] = useState(HostClientsConstants.INITIAL_INVITE_USER_STATE);
  const [inviteOrgTypeId, setInviteOrgTypeId] = useState(null);
  const [isRoleNotSelected, setIsRoleNotSelected] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Edit user states
  const [showEditUserRoleModal, setShowEditUserRoleModal] = useState(false);
  const [showEditUserDetailsModal, setShowEditUserDetailsModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [editUserRoleForm, setEditUserRoleForm] = useState({
    organizationRoleId: "",
  });
  const [editUserDetailsForm, setEditUserDetailsForm] = useState({
    username: "",
    firstName: "",
    lastName: "",
    emailAddress: "",
    msisdn: "",
    imageUrl: "",
    status: "",
    organizationRoleId: "",
  });
  const [editUserRoleLoading, setEditUserRoleLoading] = useState(false);
  const [editUserDetailsLoading, setEditUserDetailsLoading] = useState(false);
  const [editUserError, setEditUserError] = useState(null);

  // Delete user states
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  // Bulk import states
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportOrganizationId, setBulkImportOrganizationId] = useState(null);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportRole, setBulkImportRole] = useState("");
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportError, setBulkImportError] = useState(null);

  // Loading states
  const [isBookingLoading, setIsBookingLoading] = useState(false);

  // Username validation states
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameError, setUsernameError] = useState("");

  // Username validation checking with debounce
  const checkUsernameAvailability = useCallback(async usernameToCheck => {
    if (!usernameToCheck || usernameToCheck.length < 3) {
      setUsernameAvailable(null);
      setUsernameError("");
      return;
    }

    setIsCheckingUsername(true);
    setUsernameError("");

    try {
      const response = await apiV2Service.checkUsernameAvailability(usernameToCheck);
      logger.log("Invite User Username check response:", response);
      logger.log("Invite User Response data:", response.data);
      const exists = response.data?.exists || false;
      logger.log("Invite User Exists value:", exists);

      if (exists) {
        setUsernameAvailable(false);
        setUsernameError("Username is already taken");
      } else {
        setUsernameAvailable(true);
        setUsernameError("");
      }
    } catch (error) {
      logger.error("Username availability check error:", error);
      setUsernameAvailable(null);
      setUsernameError("Username is unavailable");
    } finally {
      setIsCheckingUsername(false);
    }
  }, []);

  // Debounced username validation check
  useEffect(() => {
    logger.log("Invite User Username useEffect triggered:", inviteUserForm.username);
    if (!inviteUserForm.username) {
      setUsernameAvailable(null);
      setUsernameError("");
      return;
    }

    if (inviteUserForm.username.trim() !== inviteUserForm.username) {
      setUsernameAvailable(false);
      setUsernameError("Username cannot have leading or trailing spaces");
      return;
    }

    const timeoutId = setTimeout(() => {
      logger.log("Calling checkUsernameAvailability for invite user:", inviteUserForm.username);
      checkUsernameAvailability(inviteUserForm.username);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [inviteUserForm.username, checkUsernameAvailability]);

  // Invite functionality
  const handleInviteUserInputChange = useCallback(e => {
    const { name, value } = e.target;
    setInviteUserForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const areInviteUserFieldsOkay = useCallback(() => {
    const { username, firstName, lastName, emailAddress, role } = inviteUserForm;
    // When selectedRole is set, we don't need role from form
    const hasRole = selectedRole || role;
    if (!username || !firstName || !lastName || !emailAddress || !hasRole) {
      setIsRoleNotSelected(!hasRole);
      toastUtils.error("Please fill all required fields.");
      return false;
    }

    // Check username validation
    if (username && (username.length < 3 || username.trim() !== username)) {
      setUsernameError(
        username.trim() !== username
          ? "Username cannot have leading or trailing spaces"
          : "Username must be at least 3 characters long"
      );
      return false;
    }

    if (usernameAvailable === false) {
      return false;
    }

    return true;
  }, [inviteUserForm, selectedRole, usernameAvailable]);

  const inviteUser = useCallback(
    async organizationRoles => {
      if (!areInviteUserFieldsOkay()) return;

      const currentUser = localService.getUser();
      const organizationId = currentUser?.organizationId || currentUser?.organization?.id;
      const confirmationUrl = window.location.origin + "/?action=confirm_account";

      // Use selectedRole if available (from role sections), otherwise use the form role
      const roleToUse = selectedRole ? selectedRole.code : inviteUserForm.role;

      // Find role ID from dynamic organizationRoles data or fallback to hardcoded mapping
      const role = organizationRoles.find(r => r.code === roleToUse);
      const roleId = role?.id || HostClientsConstants.ORG_ROLE_CODE_TO_ID[roleToUse];

      if (!organizationId) {
        toastUtils.error("Missing organization information. Please contact support.");
        return;
      }

      if (!roleId) {
        toastUtils.error("Invalid role selected. Please select a valid role.");
        return;
      }

      setIsBookingLoading(true);
      try {
        // Check if this is an individual customer role (organizationTypeId: 7)
        const isIndividualCustomer = role?.organizationTypeId === 7 || roleToUse === "ROLE_HOST_CUSTOMER_INDIVIDUAL";

        let response;

        if (isIndividualCustomer) {
          // Use specialized endpoint for individual customers
          const payload = {
            username: inviteUserForm.username,
            firstName: inviteUserForm.firstName,
            lastName: inviteUserForm.lastName,
            emailAddress: inviteUserForm.emailAddress,
            msisdn: inviteUserForm.msisdn || "",
            confirmationUrl,
          };

          response = await apiV2Service.addHostIndividualCustomer(organizationId, payload);
        } else {
          // Use general invitation endpoint for other roles
          const payload = {
            organizationId,
            organizationRoleId: roleId,
            username: inviteUserForm.username,
            firstName: inviteUserForm.firstName,
            lastName: inviteUserForm.lastName,
            emailAddress: inviteUserForm.emailAddress,
            msisdn: inviteUserForm.msisdn || "",
            confirmationUrl,
          };

          response = await apiV2Service.inviteUserV2(payload);
        }

        setShowInviteModal(false);
        showApiMessage(response, "invite user");
        setInviteUserForm(HostClientsConstants.INITIAL_INVITE_USER_STATE);
        setSelectedRole(null); // Reset selected role
        return { success: true };
      } catch (err) {
        logger.error("Error inviting user:", err);
        showApiMessage(err, "invite user");
        return { success: false, error: err };
      } finally {
        setIsBookingLoading(false);
      }
    },
    [areInviteUserFieldsOkay, selectedRole, inviteUserForm]
  );

  // Edit user role functionality
  const handleEditUserRole = useCallback(async user => {
    setEditUserRoleLoading(true);
    setEditUserError(null);

    try {
      // Fetch the latest user data from the API
      const response = await apiV2Service.getUserById(user.id);
      const latestUserData = response.data.data || response.data;

      setSelectedUserForEdit(latestUserData);
      setEditUserRoleForm({
        organizationRoleId: latestUserData.organizationRoleId || latestUserData.organizationRole?.id || "",
      });
      setShowEditUserRoleModal(true);
      logger.log("Latest user data for role edit:", latestUserData);
    } catch (error) {
      logger.error("Error fetching user data for role edit:", error);
      setEditUserError("Failed to load user data. Please try again.");
      // Fallback to using the provided user data
      setSelectedUserForEdit(user);
      setEditUserRoleForm({
        organizationRoleId: user.organizationRoleId || user.organizationRole?.id || "",
      });
      setShowEditUserRoleModal(true);
      showApiMessage(error, "load user");
    } finally {
      setEditUserRoleLoading(false);
    }
  }, []);

  const handleEditUserRoleInputChange = useCallback(e => {
    const { name, value } = e.target;
    setEditUserRoleForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleEditUserRoleSubmit = useCallback(async () => {
    if (!editUserRoleForm.organizationRoleId) {
      setEditUserError("Please select a role");
      return;
    }

    setEditUserRoleLoading(true);
    setEditUserError(null);

    try {
      await apiV2Service.updateUser(selectedUserForEdit.id, {
        organizationRoleId: editUserRoleForm.organizationRoleId,
      });
      showApiMessage({ status: 200, data: { message: "User role updated successfully" } }, "update user role");
      setShowEditUserRoleModal(false);
      return { success: true };
    } catch (error) {
      logger.error("Error updating user role:", error);
      setEditUserError("Failed to update user role. Please try again.");
      return { success: false, error };
    } finally {
      setEditUserRoleLoading(false);
    }
  }, [editUserRoleForm, selectedUserForEdit]);

  // Resend activation email for INACTIVE users
  const handleResendActivationEmail = useCallback(async () => {
    if (!selectedUserForEdit) return { success: false };

    setEditUserDetailsLoading(true);
    setEditUserError(null);

    try {
      const confirmationUrl = `${window.location.origin}/?action=confirm_account`;
      await apiV2Service.resendAccountConfirmation({
        username: selectedUserForEdit.username,
        confirmationUrl,
      });
      showApiMessage(
        { status: 202, data: { message: "Account confirmation email resent successfully" } },
        "resend confirmation"
      );
      setShowEditUserDetailsModal(false);
      return { success: true };
    } catch (error) {
      logger.error("Error resending activation email:", error);
      setEditUserError("Failed to resend activation email. Please try again.");
      showApiMessage(error, "resend confirmation");
      return { success: false, error };
    } finally {
      setEditUserDetailsLoading(false);
    }
  }, [selectedUserForEdit]);

  // Edit user details functionality
  const handleEditUserDetails = useCallback(async user => {
    // Show dialog immediately with basic user data
    setSelectedUserForEdit(user);
    setEditUserDetailsForm({
      username: user.username || "",
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      emailAddress: user.emailAddress || "",
      msisdn: user.msisdn || "",
      imageUrl: user.imageUrl || "",
      status: user.status || "",
      organizationRoleId: user.organizationRoleId || user.organizationRole?.id || "",
    });
    setShowEditUserDetailsModal(true);
    setEditUserError(null);
    setEditUserDetailsLoading(true);

    try {
      // Fetch the latest user data from the API in the background
      const response = await apiV2Service.getUserById(user.id);
      const latestUserData = response.data.data || response.data;

      setSelectedUserForEdit(latestUserData);
      setEditUserDetailsForm({
        username: latestUserData.username || "",
        firstName: latestUserData.firstName || "",
        lastName: latestUserData.lastName || "",
        emailAddress: latestUserData.emailAddress || "",
        msisdn: latestUserData.msisdn || "",
        imageUrl: latestUserData.imageUrl || "",
        status: latestUserData.status || "",
        organizationRoleId: latestUserData.organizationRoleId || latestUserData.organizationRole?.id || "",
      });
      logger.log("Latest user data for details edit:", latestUserData);
    } catch (error) {
      logger.error("Error fetching user data for details edit:", error);
      setEditUserError("Failed to load latest user data. Using cached data.");
      showApiMessage(error, "load user");
    } finally {
      setEditUserDetailsLoading(false);
    }
  }, []);

  const handleEditUserDetailsInputChange = useCallback(e => {
    const { name, value } = e.target;
    setEditUserDetailsForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleToggleUserStatus = useCallback(async user => {
    if (!user) return { success: false };

    setEditUserDetailsLoading(true);
    setEditUserError(null);

    try {
      const currentStatus = user.status;
      const confirmationUrl = `${window.location.origin}/?action=confirm_account`;

      if (currentStatus === "ACTIVE") {
        // Deactivate: ACTIVE -> DISABLED
        logger.log("Deactivating user:", user.id);
        await apiV2Service.updateUser(user.id, { status: "DISABLED" });
        showApiMessage({ status: 200, data: { message: "User account disabled successfully" } }, "disable user");
      } else if (currentStatus === "DISABLED") {
        // Activate: DISABLED -> ACTIVE
        logger.log("Reactivating DISABLED user:", user.id);
        await apiV2Service.reactivateUser(user.id, { confirmationUrl });
        showApiMessage({ status: 200, data: { message: "User account reactivated successfully" } }, "reactivate user");
      } else if (currentStatus === "INACTIVE") {
        // Activate: INACTIVE -> ACTIVE (Resend confirmation)
        logger.log("Resending account confirmation for INACTIVE user:", user.username);
        await apiV2Service.resendAccountConfirmation({
          username: user.username,
          confirmationUrl,
        });
        showApiMessage(
          { status: 202, data: { message: "Account confirmation email resent successfully" } },
          "resend confirmation"
        );
      }

      return { success: true };
    } catch (error) {
      logger.error("Error toggling user status:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to update user status.";
      setEditUserError(errorMessage);
      showApiMessage(error, "update status");
      return { success: false, error };
    } finally {
      setEditUserDetailsLoading(false);
    }
  }, []);

  const handleEditUserDetailsSubmit = useCallback(async () => {
    if (!editUserDetailsForm.firstName || !editUserDetailsForm.lastName || !editUserDetailsForm.emailAddress) {
      setEditUserError("Please fill in all required fields (First Name, Last Name, Email)");
      return;
    }

    setEditUserDetailsLoading(true);
    setEditUserError(null);

    try {
      const { username, status, ...updatePayload } = editUserDetailsForm;
      const oldStatus = selectedUserForEdit.status;
      const newStatus = status;

      // Check if user is being activated from INACTIVE or DISABLED status
      const isActivatingInactiveUser = oldStatus === "INACTIVE" && newStatus === "ACTIVE";
      const isActivatingDisabledUser = oldStatus === "DISABLED" && newStatus === "ACTIVE";

      if (isActivatingInactiveUser) {
        // For INACTIVE users (pending confirmation), resend confirmation email
        logger.log("Resending account confirmation for INACTIVE user:", username);
        const confirmationUrl = `${window.location.origin}/?action=confirm_account`;
        await apiV2Service.resendAccountConfirmation({
          username: username || selectedUserForEdit.username,
          confirmationUrl,
        });
        showApiMessage(
          { status: 202, data: { message: "Account confirmation email resent successfully" } },
          "resend confirmation"
        );
        setShowEditUserDetailsModal(false);
        return { success: true };
      } else if (isActivatingDisabledUser) {
        // For DISABLED users, use reactivation endpoint
        logger.log("Reactivating DISABLED user:", selectedUserForEdit.id);
        const confirmationUrl = `${window.location.origin}/?action=confirm_account`;
        await apiV2Service.reactivateUser(selectedUserForEdit.id, { confirmationUrl });
        showApiMessage({ status: 200, data: { message: "User account reactivated successfully" } }, "reactivate user");
        setShowEditUserDetailsModal(false);
        return { success: true };
      } else {
        // For all other updates (including non-status changes), use PATCH
        if (newStatus && newStatus !== oldStatus && newStatus !== "INACTIVE") {
          // If status is being changed but not to ACTIVE from INACTIVE/DISABLED
          updatePayload.status = newStatus;
        }
        // Include organizationRoleId if changed
        if (
          editUserDetailsForm.organizationRoleId &&
          editUserDetailsForm.organizationRoleId !== selectedUserForEdit.organizationRoleId
        ) {
          updatePayload.organizationRoleId = editUserDetailsForm.organizationRoleId;
        }
        await apiV2Service.updateUser(selectedUserForEdit.id, updatePayload);
        showApiMessage({ status: 200, data: { message: "User details updated successfully" } }, "update user details");
        setShowEditUserDetailsModal(false);
        return { success: true };
      }
    } catch (error) {
      logger.error("Error updating user details:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Failed to update user details. Please try again.";
      setEditUserError(errorMessage);
      showApiMessage(error, "update user");
      return { success: false, error };
    } finally {
      setEditUserDetailsLoading(false);
    }
  }, [editUserDetailsForm, selectedUserForEdit]);

  // Delete user functionality
  const handleDeleteUser = useCallback(user => {
    setUserToDelete(user);
    setShowDeleteUserModal(true);
  }, []);

  const handleDeleteUserConfirm = useCallback(async () => {
    if (!userToDelete) return;

    setDeleteUserLoading(true);

    try {
      await apiV2Service.deleteUser(userToDelete.id);
      showApiMessage({ status: 200, data: { message: "User deleted successfully" } }, "delete user");
      setShowDeleteUserModal(false);
      setUserToDelete(null);
      return { success: true };
    } catch (error) {
      logger.error("Error deleting user:", error);
      showApiMessage(error, "delete user");
      return { success: false, error };
    } finally {
      setDeleteUserLoading(false);
    }
  }, [userToDelete]);

  // Bulk import functionality
  const handleBulkImport = useCallback(organizationId => {
    setBulkImportOrganizationId(organizationId);
    setBulkImportFile(null);
    setBulkImportRole("");
    setBulkImportError(null);
    setShowBulkImportModal(true);
  }, []);

  const handleBulkImportSubmit = useCallback(async () => {
    if (!bulkImportFile || !bulkImportRole) {
      setBulkImportError("Please select a file and role");
      return;
    }

    setBulkImportLoading(true);
    setBulkImportError(null);

    try {
      const formData = new FormData();
      formData.append("file", bulkImportFile);
      formData.append("organizationId", bulkImportOrganizationId);
      formData.append("role", bulkImportRole);

      // Call your bulk import API endpoint
      // await apiV2Service.bulkImportUsers(formData)

      toastUtils.success("Bulk import completed successfully");
      setShowBulkImportModal(false);
      return { success: true };
    } catch (error) {
      logger.error("Error during bulk import:", error);
      setBulkImportError("Failed to import users. Please try again.");
      return { success: false, error };
    } finally {
      setBulkImportLoading(false);
    }
  }, [bulkImportFile, bulkImportRole, bulkImportOrganizationId]);

  return {
    // Invite states
    showInviteModal,
    setShowInviteModal,
    inviteUserForm,
    setInviteUserForm,
    inviteOrgTypeId,
    setInviteOrgTypeId,
    isRoleNotSelected,
    selectedRole,
    setSelectedRole,
    isBookingLoading,

    // Edit user states
    showEditUserRoleModal,
    setShowEditUserRoleModal,
    showEditUserDetailsModal,
    setShowEditUserDetailsModal,
    selectedUserForEdit,
    editUserRoleForm,
    editUserDetailsForm,
    setEditUserDetailsForm,
    editUserRoleLoading,
    editUserDetailsLoading,
    editUserError,

    // Delete user states
    showDeleteUserModal,
    setShowDeleteUserModal,
    userToDelete,
    deleteUserLoading,

    // Bulk import states
    showBulkImportModal,
    setShowBulkImportModal,
    bulkImportOrganizationId,
    bulkImportFile,
    setBulkImportFile,
    bulkImportRole,
    setBulkImportRole,
    bulkImportLoading,
    bulkImportError,

    // Username validation states
    isCheckingUsername,
    usernameAvailable,
    usernameError,

    // Actions
    handleInviteUserInputChange,
    inviteUser,
    handleEditUserRole,
    handleEditUserRoleInputChange,
    handleEditUserRoleSubmit,
    handleEditUserDetails,
    handleEditUserDetailsInputChange,
    handleEditUserDetailsSubmit,
    handleDeleteUser,
    handleDeleteUserConfirm,
    handleBulkImport,
    handleBulkImportSubmit,
    handleResendActivationEmail,
    handleToggleUserStatus,
  };
};
